import React, { useState } from 'react';
import { Button, Card, Descriptions, Form, Input, Select, Space, Tag, Typography, message, List } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { enterpriseMemoryService } from '@/services/enterpriseMemory.service';
import { MemoryReasonTags } from '@/components/ai-agent/MemoryReasonTags';
import api from '@/services/api.service';

function RetrieveReasonPreview({
  memoryId,
  title,
  summary,
}: {
  memoryId: string;
  title: string;
  summary?: string;
}) {
  const [reasons, setReasons] = React.useState<string[]>([]);
  const [loading, setLoading] = React.useState(false);
  const run = async () => {
    setLoading(true);
    try {
      const hits = await enterpriseMemoryService.retrieve({
        keyword: title || summary?.slice(0, 80) || '',
        hybrid: true,
        limit: 10,
      });
      const match = hits.find((h) => h.memory.id === memoryId);
      setReasons(match?.reasons?.length ? match.reasons : ['Not in top retrieve results for this keyword']);
    } catch {
      setReasons(['Retrieve unavailable']);
    } finally {
      setLoading(false);
    }
  };
  return (
    <Space direction="vertical">
      <Button size="small" loading={loading} onClick={() => void run()}>
        Test retrieval
      </Button>
      {reasons.length ? (
        <MemoryReasonTags items={[{ id: memoryId, title, reasons }]} />
      ) : (
        <Text type="secondary">Run a retrieve test to see human-readable match reasons.</Text>
      )}
    </Space>
  );
}

const { Title, Paragraph, Text } = Typography;

export default function EnterpriseMemoryDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [relForm] = Form.useForm();

  const query = useQuery({
    queryKey: ['enterprise-memory', id],
    queryFn: () => enterpriseMemoryService.getMemory(id!),
    enabled: Boolean(id),
  });

  const relQuery = useQuery({
    queryKey: ['enterprise-memory-rel', id],
    queryFn: async () => {
      const body = (await api.get(`/enterprise-memory/${id}/relationships`)) as {
        data?: unknown[];
      };
      return body.data || [];
    },
    enabled: Boolean(id),
  });

  const timelineQuery = useQuery({
    queryKey: ['enterprise-memory-timeline', id],
    queryFn: async () => {
      const body = (await api.get(`/enterprise-memory/${id}/timeline`)) as { data?: any };
      return body.data;
    },
    enabled: Boolean(id),
  });

  const publishMut = useMutation({
    mutationFn: () => enterpriseMemoryService.publish(id!),
    onSuccess: () => {
      message.success('Published');
      qc.invalidateQueries({ queryKey: ['enterprise-memory', id] });
      qc.invalidateQueries({ queryKey: ['enterprise-memory-list'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const feedbackMut = useMutation({
    mutationFn: (rating: string) => enterpriseMemoryService.feedback(id!, rating),
    onSuccess: () => message.success('Feedback recorded (review signal only)'),
    onError: (e: Error) => message.error(e.message),
  });

  const detectMut = useMutation({
    mutationFn: async () => {
      const body = (await api.post(`/enterprise-memory/${id}/detect-contradictions`, {})) as {
        data?: { createdCount?: number };
        message?: string;
      };
      return body.data;
    },
    onSuccess: (data) => {
      message.success(`Contradiction scan complete (${data?.createdCount ?? 0} new)`);
      qc.invalidateQueries({ queryKey: ['enterprise-memory', id] });
      relQuery.refetch();
    },
    onError: (e: Error) => message.error(e.message),
  });

  const addRelMut = useMutation({
    mutationFn: async (values: Record<string, string>) => {
      await api.post(`/enterprise-memory/${id}/relationships`, {
        toMemoryId: values.toMemoryId || undefined,
        toEntityType: values.toEntityType || undefined,
        toEntityId: values.toEntityId || undefined,
        relationshipType: values.relationshipType,
        notes: values.notes,
      });
    },
    onSuccess: () => {
      message.success('Relationship added');
      relForm.resetFields();
      relQuery.refetch();
    },
    onError: (e: Error) => message.error(e.message),
  });

  const m = query.data as any;
  const contradictions = ((m?.evidence && timelineQuery.data) || null) as any;

  return (
    <div style={{ padding: 24 }}>
      <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/ai/enterprise-memory')}>
        Back
      </Button>
      {m && (
        <>
          <Space style={{ width: '100%', justifyContent: 'space-between', margin: '12px 0' }}>
            <Title level={3} style={{ margin: 0 }}>
              {m.title}
            </Title>
            <Space>
              {(m.status === 'UNDER_REVIEW' || m.status === 'VALIDATED') && (
                <Button type="primary" loading={publishMut.isPending} onClick={() => publishMut.mutate()}>
                  Publish
                </Button>
              )}
              <Button loading={detectMut.isPending} onClick={() => detectMut.mutate()}>
                Detect contradictions
              </Button>
              <Button onClick={() => feedbackMut.mutate('helpful')}>Helpful</Button>
              <Button onClick={() => feedbackMut.mutate('not_helpful')}>Not helpful</Button>
            </Space>
          </Space>
          <Tag>{m.status}</Tag> <Tag>Trust {Number(m.trustScore || 0).toFixed(0)}</Tag>
          <Paragraph style={{ marginTop: 16 }}>{m.summary}</Paragraph>
          {m.detailedKnowledge && <Paragraph type="secondary">{m.detailedKnowledge}</Paragraph>}
          <Card title="Why this memory surfaces" size="small" style={{ marginTop: 16 }}>
            <RetrieveReasonPreview memoryId={m.id} title={m.title} summary={m.summary} />
          </Card>
          <Card title="Metadata" size="small" style={{ marginTop: 16 }}>
            <Descriptions column={2} size="small">
              <Descriptions.Item label="Type">{m.memoryType}</Descriptions.Item>
              <Descriptions.Item label="Domain">{m.domain}</Descriptions.Item>
              <Descriptions.Item label="Subject">{m.subjectLabel || '—'}</Descriptions.Item>
              <Descriptions.Item label="Effective from">{m.effectiveFrom || '—'}</Descriptions.Item>
              <Descriptions.Item label="Version">{m.contentVersion}</Descriptions.Item>
              <Descriptions.Item label="Schema">{m.schemaVersion}</Descriptions.Item>
            </Descriptions>
          </Card>
          <Card title="Evidence" size="small" style={{ marginTop: 16 }}>
            <ul>
              {(m.evidence || []).map((e: any) => (
                <li key={e.id}>
                  {e.source_type}: {e.document_number || e.source_id}
                </li>
              ))}
            </ul>
          </Card>
          <Card title="Knowledge graph — relationships" size="small" style={{ marginTop: 16 }}>
            <List
              size="small"
              loading={relQuery.isLoading}
              dataSource={(relQuery.data as any[]) || []}
              locale={{ emptyText: 'No relationships yet' }}
              renderItem={(item: any) => (
                <List.Item>
                  <Text>
                    {item.relationship_type}: {item.to_memory_id || `${item.to_entity_type}/${item.to_entity_id}`}
                    {item.notes ? ` — ${item.notes}` : ''}
                  </Text>
                </List.Item>
              )}
            />
            <Form
              form={relForm}
              layout="inline"
              style={{ marginTop: 12 }}
              initialValues={{ relationshipType: 'related_to' }}
              onFinish={(v) => addRelMut.mutate(v)}
            >
              <Form.Item name="relationshipType" rules={[{ required: true }]}>
                <Select
                  style={{ width: 160 }}
                  options={[
                    { value: 'related_to', label: 'related_to' },
                    { value: 'mitigates', label: 'mitigates' },
                    { value: 'caused_by', label: 'caused_by' },
                    { value: 'supersedes', label: 'supersedes' },
                  ]}
                />
              </Form.Item>
              <Form.Item name="toMemoryId">
                <Input placeholder="To memory id" style={{ width: 220 }} />
              </Form.Item>
              <Form.Item name="notes">
                <Input placeholder="Notes" style={{ width: 180 }} />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={addRelMut.isPending}>
                Add link
              </Button>
            </Form>
          </Card>
          <Card title="Reviews / usage signals" size="small" style={{ marginTop: 16 }}>
            <Text type="secondary">
              Reviews: {timelineQuery.data?.reviews?.length ?? 0} · Usage: {timelineQuery.data?.usage?.length ?? 0} ·
              Feedback: {timelineQuery.data?.feedback?.length ?? 0}
              {contradictions ? '' : ''}
            </Text>
          </Card>
        </>
      )}
    </div>
  );
}
